import type { LocalizedString } from "../i18n";

/**
 * Minimum account value per wrapper type (QL-362). A wrapper type can carry a
 * `minAccountValue` in the collection currency; at flow submit every account
 * the flow's one-time orders would land in must hold at least that much once
 * those orders are done. Submit is the gate — saving a portfolio below the
 * minimum is allowed so an account can be built up product by product.
 *
 * The rule lives here so the API (flow-completion engine + portfolio payloads)
 * and the web UI (inline warnings) share one definition, mirroring `min-buy`.
 */

/** `kind` leaves room for a future maximum without renaming the type. */
export type AccountMinimumViolationKind = "below_minimum";

/** One account below its wrapper type's minimum, as served on portfolio payloads. */
export type AccountMinimumViolation = {
  kind: AccountMinimumViolationKind;
  wrapperSyncKey: string;
  wrapperNameI18n: LocalizedString;
  /** What the account would hold after the flow's one-time orders, in `currency`. */
  postValue: number;
  /** The minimum as compared — the wrapper type's value as entered, NOT converted into `currency`. */
  minimum: number;
  /**
   * Currency of the comparison — the flow currency for accounts the flow will
   * open, a follow-up source account's own currency when the bank recorded one.
   */
  currency: string;
  /** `minimum - postValue`, rounded to two decimals. */
  shortfall: number;
  /** Every goal portfolio feeding this account — incl. the follow-up portfolio tracking it. */
  portfolioIds: string[];
};

/** Largest storable `minAccountValue` — the column is a Postgres `integer` (int4). */
export const MAX_MIN_ACCOUNT_VALUE = 2_147_483_647;

/** Account values are compared and reported at two decimals. */
export const roundAccountValue = (value: number): number => Math.round(value * 100) / 100;

/**
 * Whether an account's post-transaction value breaches a minimum. Breach iff
 * `postValue < minimum` at two decimals — equal passes, and zero IS a breach
 * (no full-exit exemption, decision 7). A null minimum never breaches.
 */
export function isBelowAccountMinimum(
  postValue: number,
  minimum: number | null | undefined,
): boolean {
  if (minimum == null || !Number.isFinite(minimum)) return false;
  return roundAccountValue(postValue) < roundAccountValue(minimum);
}

/** The amount missing for `postValue` to reach `minimum`, at two decimals (0 when not below). */
export function accountMinimumShortfall(postValue: number, minimum: number): number {
  return isBelowAccountMinimum(postValue, minimum) ? roundAccountValue(minimum - postValue) : 0;
}
